import { useEffect, useMemo } from 'react'
import useSearchBurnEnabled from 'hooks/useSearchBurnEnabled'
import useSearchBurnPoints, { resolveSearchBurnResult } from 'hooks/useSearchBurnPoints'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import buildBurnKey from 'loyalty/lib/pointsCalculation/buildBurnKey'
import { SET_BUNDLED_BURN_BATCH_RESULT } from 'actions/actionConstants'
import type { SearchBurnResult } from 'hooks/useSearchBurnPoints'

interface UseSearchBundledBurnPointsOptions {
  disabled?: boolean
}

/**
 * Calculates burn points for a bundled hotel+flight package on the search page.
 *
 * Sends hotel price and flight price as separate requests to match how checkout
 * splits the package into two line items, then combines the resulting priceInPoints
 * and applies the user's burn preference once on the total.
 */
export default function useSearchBundledBurnPoints(
  packagePrice: number,
  hotelLoyaltyProductType: App.LoyaltyProductType,
  flightPrice: number,
  hotelMx?: string,
  options: UseSearchBundledBurnPointsOptions = {},
): SearchBurnResult {
  const { disabled = false } = options
  const shouldUseSplitBurn = !disabled && flightPrice > 0
  const hotelPrice = Math.max(0, packagePrice - flightPrice)

  const dispatch = useAppDispatch()
  const { hasSelectedBurnToggle, pointsToBurn: contextPointsToBurn } = useSearchBurnEnabled()
  const userPoints = useAppSelector(state => state.loyalty.account.data?.points ?? 0)
  const customerId = useAppSelector(state => state.auth.account.memberId ?? '')

  const hotelBurnResult = useSearchBurnPoints(hotelPrice, hotelLoyaltyProductType, hotelMx, { disabled })
  const flightBurnResult = useSearchBurnPoints(flightPrice, 'int_economy', undefined, { disabled: disabled || !shouldUseSplitBurn })

  const bundleKey = useMemo(() => {
    if (disabled || !shouldUseSplitBurn || !customerId) return undefined
    const hotelKey = buildBurnKey({ price: hotelPrice, loyaltyProductType: hotelLoyaltyProductType, mx: hotelMx }, customerId)
    const flightKey = buildBurnKey({ price: flightPrice, loyaltyProductType: 'int_economy' }, customerId)
    return `bundle:${hotelKey}:${flightKey}`
  }, [disabled, shouldUseSplitBurn, hotelPrice, hotelLoyaltyProductType, hotelMx, flightPrice, customerId])

  const hotelInPoints = hotelBurnResult.priceInPoints
  const flightInPoints = flightBurnResult.priceInPoints
  const combinedPriceInPoints = shouldUseSplitBurn && hotelInPoints !== undefined && flightInPoints !== undefined
    ? hotelInPoints + flightInPoints
    : undefined

  useEffect(() => {
    if (!bundleKey || combinedPriceInPoints === undefined) return
    dispatch({ type: SET_BUNDLED_BURN_BATCH_RESULT, burnBatchKey: bundleKey, priceInPoints: combinedPriceInPoints })
  }, [dispatch, bundleKey, combinedPriceInPoints])

  return useMemo(() => {
    if (disabled || !shouldUseSplitBurn) return hotelBurnResult
    return resolveSearchBurnResult({
      priceInPoints: combinedPriceInPoints,
      hasSelectedBurnToggle,
      disabled,
      contextPointsToBurn,
      userPoints,
    })
  }, [disabled, shouldUseSplitBurn, hotelBurnResult, combinedPriceInPoints, hasSelectedBurnToggle, contextPointsToBurn, userPoints])
}
